/* eslint-disable react-hooks/exhaustive-deps */
import axios from 'axios';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { saveAll, saveMany } from '../store/items';
import { isGlobalAdmin } from '../utils/roles';
import { canAccessItem } from '../utils/item';
import {
  getStoredAuthUser,
  isUniverseAdmin,
  isUserInList,
} from '../utils/auth';
import {
  ensureUserStorageHydrated,
  getStorageJSON,
  getStorageValue,
  migrateStorageKeysToCurrentUser,
  setStorageJSON,
} from '../utils/userStorage';
import { hasAccessConfig } from '../utils/auth';
import { NavigationManagePassword } from '../components/navigation/navigation.Passwords';
import {
  FaChevronDown,
  FaEye,
  FaChevronUp,
  FaHistory,
  FaEyeSlash,
  FaGripVertical,
  FaKey,
  FaPlus,
  FaStar,
  FaThumbtack,
  FaTrash,
  FaSearch,
} from 'react-icons/fa';
import { setOpenModales } from '../store/global';
import { logoSVG } from '../content/logo.svg';
import { ThemeChange } from '../components/theme/theme.Change';
import { CardItemHome } from '../components/home/cardItem';
import { ModalGlobal } from '../components/modal/modal.Global';
import { InputSimple } from '../components/form/input.Simple';
import { BtnRound } from '../components/form/btn.Round';
import { BtnSimple } from '../components/form/btn.Simple';
import { createNewItem } from '../services/item';
import { NavigationDisplayItems } from '../components/navigation/navigation.displayItems';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCenter,
  MeasuringStrategy,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToFirstScrollableAncestor } from '@dnd-kit/modifiers';
import {
  arrayMove,
  horizontalListSortingStrategy,
  rectSortingStrategy,
  SortableContext,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import uuid from 'react-uuid';

const DEFAULT_COLUMN_ID = 'home-default';
const HOME_V2_ENABLED = true;
const HOME_DROP_SECTION_IDS = new Set(['pinned', 'favorites', 'hidden']);

const HomeSortableItem = props => {
  const { item, columnId } = props;
  const campaign = useSelector(state => state.items[item?.campaign]);
  const world = useSelector(state => state.items[item?.world]);
  const universe = useSelector(state => state.items[item?.universe]);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item?._id, data: { type: 'item', columnId } });

  const accessBlocksPublic =
    (item?.type === 'campaign' && item?.access?.visibility === 'private') ||
    (item?.type === 'universe' && item?.access?.visibility === 'private') ||
    campaign?.access?.visibility === 'private' ||
    universe?.access?.visibility === 'private';
  const legacyPublic =
    !accessBlocksPublic &&
    !item?.password &&
    (!item?.campaign || (campaign && !campaign?.password)) &&
    (!item?.world || (world && !world?.password)) &&
    (!item?.universe || (universe && !universe?.password));
  const isVisible =
    isGlobalAdmin() ||
    item?.type === 'universe' ||
    canAccessItem(item, campaign, world, universe) ||
    legacyPublic;
  if (!isVisible) {
    return null;
  }

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`w-full relative ${isDragging ? 'opacity-60' : ''}`}
    >
      <div
        className='absolute right-1 top-1 z-20 rounded bg-main bg-opacity-70 p-05 text-contrast text-opacity-80 cursor-grab active:cursor-grabbing'
        onClick={e => e.stopPropagation()}
        onPointerDown={e => e.stopPropagation()}
        aria-label='Réorganiser'
        data-column-id={columnId}
        {...attributes}
        {...listeners}
      >
        <FaGripVertical />
      </div>
      <CardItemHome
        item={item}
        isUniverse={item?.type === 'universe'}
      />
    </div>
  );
};

const HomeSortableColumn = props => {
  const {
    column,
    children,
    onDelete,
    isEditing,
    editingLabel,
    onStartEdit,
    onChangeEdit,
    onCommitEdit,
    onCancelEdit,
  } = props;
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: column.id,
    data: { type: 'column' },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`w-350 min-w-350 h-full max-h-full rounded-lg bg-main bg-opacity-40 p-1 flex flex-col ${
        isDragging ? 'opacity-60' : ''
      }`}
    >
      <div className='flex items-center gap-05 mb-05'>
        {column.id !== DEFAULT_COLUMN_ID && (
          <div
            className='p-05 text-secondary text-opacity-70 hover:text-opacity-100 cursor-pointer'
            onClick={e => e.stopPropagation()}
            onDoubleClick={e => {
              e.stopPropagation();
              onDelete?.(column);
            }}
            aria-label='Supprimer la colonne'
            title='Supprimer la colonne'
          >
            <FaTrash />
          </div>
        )}
        {isEditing ? (
          <input
            className='flex-1 bg-main bg-opacity-50 text-contrast text-opacity-90 text-sm font-condensed uppercase px-05 py-02 rounded flex-1'
            value={editingLabel}
            onChange={e => onChangeEdit?.(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                onCommitEdit?.(column);
              }
              if (e.key === 'Escape') {
                e.preventDefault();
                onCancelEdit?.();
              }
            }}
            onBlur={() => onCommitEdit?.(column)}
            autoFocus
          />
        ) : (
          <div
            className='flex-1 font-condensed font-bold text-sm uppercase text-contrast text-opacity-80 cursor-text'
            onDoubleClick={e => {
              e.stopPropagation();
              onStartEdit?.(column);
            }}
            title='Double-clique pour renommer'
          >
            {column.label}
          </div>
        )}
        <div
          className='rounded bg-main bg-opacity-70 p-05 text-contrast text-opacity-80 cursor-grab active:cursor-grabbing'
          onClick={e => e.stopPropagation()}
          onPointerDown={e => e.stopPropagation()}
          aria-label='Réorganiser la colonne'
          {...attributes}
          {...listeners}
        >
          <FaGripVertical />
        </div>
      </div>
      <div className='flex flex-col gap-1 overflow-y-auto min-h-0 flex-1'>
        {children}
      </div>
    </div>
  );
};

const cloneColumns = columns =>
  columns.map(column => ({
    ...column,
    itemIds: Array.isArray(column.itemIds) ? [...column.itemIds] : [],
  }));

const restrictColumnDrag = ({ transform, active }) => {
  if (active?.data?.current?.type === 'column') {
    return { ...transform, y: 0 };
  }
  return transform;
};

const restrictToScrollForColumns = args => {
  if (args.active?.data?.current?.type === 'column') {
    return restrictToFirstScrollableAncestor(args);
  }
  return args.transform;
};

function Home() {
  const navigate = useNavigate();
  const items = useSelector(state => state.items);
  const theme = useSelector(state => state.global.theme);
  const isMobile = useSelector(state => state.global.isMobile);
  const history = useSelector(state => state.global.history || []);
  const dispatch = useDispatch();
  const columnsScrollRef = useRef(null);

  const [universes, setUniverses] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [authUserState, setAuthUserState] = useState(getStoredAuthUser());
  const [homeColumns, setHomeColumns] = useState(() => {
    const savedColumns = getStorageJSON('homeColumns', null);
    if (Array.isArray(savedColumns)) {
      return savedColumns;
    }
    return [];
  });
  const [hasLoadedFavorites, setHasLoadedFavorites] = useState(false);
  const [hasLoadedUniverses, setHasLoadedUniverses] = useState(false);
  const [newUniverse, setNewUniverse] = useState({
    label: '',
    password: '',
  });
  const [favoriteUrl, setFavoriteUrl] = useState('');
  const [editingColumnId, setEditingColumnId] = useState(null);
  const [editingColumnLabel, setEditingColumnLabel] = useState('');
  const [collapsedColumns, setCollapsedColumns] = useState({});
  const [homeSearch, setHomeSearch] = useState('');
  const [homeTypeFilter, setHomeTypeFilter] = useState('all');
  const [homeOnlyFavorites, setHomeOnlyFavorites] = useState(false);
  const [recentItemIds, setRecentItemIds] = useState(() => {
    const saved = getStorageJSON('recentItemIds', []);
    return Array.isArray(saved) ? saved : [];
  });
  const [hiddenSectionCollapsed, setHiddenSectionCollapsed] = useState(true);
  const [mobileSectionsCollapsed, setMobileSectionsCollapsed] = useState({});
  const [pinnedItemIds, setPinnedItemIds] = useState(() => {
    const saved = getStorageJSON('pinnedItemIds', []);
    return Array.isArray(saved) ? saved : [];
  });
  const [hiddenItemIds, setHiddenItemIds] = useState(() => {
    const saved = getStorageJSON('hiddenItemIds', []);
    return Array.isArray(saved) ? saved : [];
  });
  const [actionErrors, setActionErrors] = useState({
    universe: '',
    favorite: '',
  });
  const [scrollMetrics, setScrollMetrics] = useState({
    scrollLeft: 0,
    scrollWidth: 0,
    clientWidth: 0,
  });
  const [isDraggingScrollbar, setIsDraggingScrollbar] = useState(false);
  const [activeDragItemId, setActiveDragItemId] = useState(null);
  const [activeHomeDropSection, setActiveHomeDropSection] = useState(null);
  const scrollDragDataRef = useRef({ startX: 0, startLeft: 0 });
  const prevFavoritesRef = useRef(new Set());
  const knownHomeItemIdsRef = useRef(new Set());
  const authUserId = authUserState?._id;
  const shouldOffsetHistoryFooter = useMemo(() => {
    if (isMobile || typeof window === 'undefined' || !window.matchMedia) {
      return false;
    }
    const isStandalonePwa = window.matchMedia(
      '(display-mode: standalone)',
    ).matches;
    const isTouchDevice = window.matchMedia('(pointer: coarse)').matches;
    const isTabletOrLarger = window.matchMedia('(min-width: 900px)').matches;
    return isStandalonePwa && isTouchDevice && isTabletOrLarger;
  }, [isMobile]);

  const getUniverses = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/items/universes`,
      );
      res.data.forEach(universe => {
        dispatch(saveAll({ item: universe }));
      });
      setUniverses(
        res.data.filter(item => {
          if (item.type !== 'universe') return false;
          if (isGlobalAdmin()) return true;
          const hasAccess = hasAccessConfig(item);
          if (hasAccess) {
            return isUniverseAdmin(item, authUserId);
          }
          return item?.password === window.localStorage.getItem('mj_access');
        }),
      );
    } finally {
      setHasLoadedUniverses(true);
    }
  };

  const getAllUniversesItems = () => {
    setUniverses(
      Object.values(items).filter(item => {
        if (item.type !== 'universe') return false;
        if (isGlobalAdmin()) return true;
        const hasAccess = hasAccessConfig(item);
        if (hasAccess) {
          return isUniverseAdmin(item, authUserId);
        }
        return item?.password === window.localStorage.getItem('mj_access');
      }),
    );
  };

  useEffect(() => {
    getUniverses();
    getAllUniversesItems();
  }, []);

  useEffect(() => {
    const token = window.localStorage.getItem('auth_token');
    if (!token || authUserState) return;
    const fetchMe = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/auth/me`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        if (res?.data?.user) {
          window.localStorage.setItem(
            'auth_user',
            JSON.stringify(res.data.user),
          );
          migrateStorageKeysToCurrentUser([
            'favorites',
            'recentItemIds',
            'homeColumns',
            'passwords',
            'currentTheme',
            'pinnedItemIds',
            'hiddenItemIds',
          ]);
          await ensureUserStorageHydrated();
          const syncedPinned = getStorageJSON('pinnedItemIds', []);
          if (Array.isArray(syncedPinned)) {
            setPinnedItemIds(syncedPinned);
          }
          const syncedRecent = getStorageJSON('recentItemIds', []);
          if (Array.isArray(syncedRecent)) {
            setRecentItemIds(syncedRecent);
          }
          const syncedHidden = getStorageJSON('hiddenItemIds', []);
          if (Array.isArray(syncedHidden)) {
            setHiddenItemIds(syncedHidden);
          }
          setAuthUserState(res.data.user);
        }
      } catch (e) {
        const status = e?.response?.status;
        if (status === 401 || status === 403) {
          window.localStorage.removeItem('auth_token');
          window.localStorage.removeItem('auth_user');
          setAuthUserState(null);
        }
      }
    };
    fetchMe();
  }, [authUserState]);

  useEffect(() => {
    getAllUniversesItems();
  }, [authUserId]);

  useEffect(() => {
    if (!authUserId) return;
    const hydrate = async () => {
      await ensureUserStorageHydrated();
      const syncedFavorites = getStorageJSON('favorites', []);
      if (Array.isArray(syncedFavorites)) {
        setFavorites(Array.from(new Set(syncedFavorites)));
      }
      const syncedColumns = getStorageJSON('homeColumns', []);
      if (Array.isArray(syncedColumns)) {
        setHomeColumns(syncedColumns);
      }
      const syncedRecent = getStorageJSON('recentItemIds', []);
      if (Array.isArray(syncedRecent)) {
        setRecentItemIds(syncedRecent);
      }
      const syncedPinned = getStorageJSON('pinnedItemIds', []);
      if (Array.isArray(syncedPinned)) {
        setPinnedItemIds(syncedPinned);
      }
      const syncedHidden = getStorageJSON('hiddenItemIds', []);
      if (Array.isArray(syncedHidden)) {
        setHiddenItemIds(syncedHidden);
      }
    };
    hydrate();
  }, [authUserId]);

  useEffect(() => {
    if (!authUserId) return;
    const fetchAdminCampaigns = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/items/admin-campaigns/${authUserId}`,
        );
        if (Array.isArray(res.data)) {
          dispatch(saveMany({ items: res.data, log: 'admin campaigns' }));
        }
      } catch (e) {}
    };
    fetchAdminCampaigns();
  }, [authUserId, dispatch]);

  const isValidObjectId = id => /^[a-f0-9]{24}$/i.test(id);
  const missingItemIdsRef = useRef(new Set());
  const getItem = async id => {
    if (!isValidObjectId(id) || missingItemIdsRef.current.has(id)) {
      return null;
    }
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/items/${id}`);
      if (res?.data?._id) {
        dispatch(saveAll({ item: res.data }));
        return res.data;
      }
      missingItemIdsRef.current.add(id);
      return null;
    } catch (error) {
      if (error?.response?.status === 404) {
        missingItemIdsRef.current.add(id);
      }
      return null;
    }
  };

  useEffect(() => {
    const missing = favorites.filter(favorite => !items[favorite]);
    if (missing.length === 0) {
      return;
    }
    const fetchMissing = async () => {
      try {
      const res = await axios.post(
          `${process.env.REACT_APP_API_URL}/items/batch`,
          { ids: missing },
        );
        if (Array.isArray(res.data)) {
          res.data.forEach(item => {
            if (item?._id) {
              dispatch(saveAll({ item }));
            }
          });
        } else {
          missing.forEach(favorite => {
            if (!items[favorite]) {
              getItem(favorite);
            }
          });
        }
      } catch (e) {
        missing.forEach(favorite => {
          if (!items[favorite]) {
            getItem(favorite);
          }
        });
      }
    };
    fetchMissing();
  }, [favorites, items, dispatch]);

  useEffect(() => {
    if (favorites.length === 0) {
      return;
    }
    const parentIds = new Set();
    favorites.forEach(favoriteId => {
      const item = items[favoriteId];
      if (!item) return;
      if (item.campaign) parentIds.add(item.campaign);
      if (item.world) parentIds.add(item.world);
      if (item.universe) parentIds.add(item.universe);
    });
    const missingParents = Array.from(parentIds).filter(id => id && !items[id]);
    if (missingParents.length === 0) {
      return;
    }
    const fetchParents = async () => {
      try {
        const res = await axios.post(
          `${process.env.REACT_APP_API_URL}/items/batch`,
          { ids: missingParents },
        );
        if (Array.isArray(res.data)) {
          res.data.forEach(item => {
            if (item?._id) {
              dispatch(saveAll({ item }));
            }
          });
        }
      } catch (e) {
        // ignore, parents will be fetched on demand elsewhere
      }
    };
    fetchParents();
  }, [favorites, items, dispatch]);

  useEffect(() => {
    const missing = recentItemIds.filter(
      recentId => isValidObjectId(recentId) && !items[recentId],
    );
    if (missing.length === 0) {
      return;
    }
    const fetchMissing = async () => {
      try {
        const res = await axios.post(
          `${process.env.REACT_APP_API_URL}/items/batch`,
          { ids: missing },
        );
        if (Array.isArray(res.data)) {
          res.data.forEach(item => {
            if (item?._id) {
              dispatch(saveAll({ item }));
            }
          });
        } else {
          missing.forEach(recentId => {
            if (!items[recentId]) {
              getItem(recentId);
            }
          });
        }
      } catch (e) {
        missing.forEach(recentId => {
          if (!items[recentId]) {
            getItem(recentId);
          }
        });
      }
    };
    fetchMissing();
  }, [recentItemIds, items, dispatch]);

  useEffect(() => {
    if (recentItemIds.length === 0) {
      return;
    }
    const parentIds = new Set();
    recentItemIds.forEach(recentId => {
      const item = items[recentId];
      if (!item) return;
      if (item.campaign && isValidObjectId(item.campaign))
        parentIds.add(item.campaign);
      if (item.world && isValidObjectId(item.world)) parentIds.add(item.world);
      if (item.universe && isValidObjectId(item.universe))
        parentIds.add(item.universe);
    });
    const missingParents = Array.from(parentIds).filter(id => id && !items[id]);
    if (missingParents.length === 0) {
      return;
    }
    const fetchParents = async () => {
      try {
        const res = await axios.post(
          `${process.env.REACT_APP_API_URL}/items/batch`,
          { ids: missingParents },
        );
        if (Array.isArray(res.data)) {
          res.data.forEach(item => {
            if (item?._id) {
              dispatch(saveAll({ item }));
            }
          });
        }
      } catch (e) {
        // ignore, parents will be fetched on demand elsewhere
      }
    };
    fetchParents();
  }, [recentItemIds, items, dispatch]);

  useEffect(() => {
    const storedFavorites = getStorageJSON('favorites', []);
    if (Array.isArray(storedFavorites)) {
      setFavorites(Array.from(new Set(storedFavorites)));
    }
    setHasLoadedFavorites(true);
  }, []);

  useEffect(() => {
    const mjPassword = getStorageValue('mj_access');
    if (mjPassword) {
      setNewUniverse(prev => ({ ...prev, password: mjPassword }));
    }
  }, []);

  useEffect(() => {
    if (!hasLoadedFavorites) {
      return;
    }
    setStorageJSON('favorites', favorites);
  }, [favorites, hasLoadedFavorites]);

  const updateScrollMetrics = () => {
    const container = columnsScrollRef.current;
    if (!container) return;
    setScrollMetrics({
      scrollLeft: container.scrollLeft,
      scrollWidth: container.scrollWidth,
      clientWidth: container.clientWidth,
    });
  };

  useEffect(() => {
    const container = columnsScrollRef.current;
    if (!container) return;

    updateScrollMetrics();
    container.addEventListener('scroll', updateScrollMetrics);
    window.addEventListener('resize', updateScrollMetrics);
    return () => {
      container.removeEventListener('scroll', updateScrollMetrics);
      window.removeEventListener('resize', updateScrollMetrics);
    };
  }, []);

  useEffect(() => {
    const raf = requestAnimationFrame(updateScrollMetrics);
    return () => cancelAnimationFrame(raf);
  }, [homeColumns, favorites, universes]);

  useEffect(() => {
    setStorageJSON('homeColumns', homeColumns);
  }, [homeColumns]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
  );

  const extractItemId = value => {
    if (!value) return null;
    const trimmed = value.trim();
    if (!trimmed) return null;
    let pathname = trimmed;
    try {
      const url = new URL(trimmed);
      pathname = url.pathname;
    } catch (e) {
      pathname = trimmed;
    }
    const match = pathname.match(/\/item\/([^/?#]+)/);
    if (match?.[1]) {
      return match[1];
    }
    if (!pathname.includes('/')) {
      return pathname;
    }
    return null;
  };

  const isPasswordValid = value => {
    if (authUserId || isGlobalAdmin()) return true;
    if (!value) return false;
    return value.length >= 8 && /[^A-Za-z0-9]/.test(value);
  };

  const handleCreateUniverse = async () => {
    const label = newUniverse.label.trim();
    const mjPassword = getStorageValue('mj_access');
    const isAuthenticated = Boolean(authUserId);
    const password = isAuthenticated
      ? ''
      : newUniverse.password.trim() || mjPassword || '';
    setActionErrors({ universe: '', favorite: '' });

    if (!label) {
      setActionErrors({
        universe: "Le nom de l'univers est requis.",
        favorite: '',
      });
      return;
    }
    if (!isAuthenticated && !isPasswordValid(password)) {
      setActionErrors({
        universe:
          'Mot de passe trop faible (8 caractères min. + 1 caractère spécial).',
        favorite: '',
      });
      return;
    }

    try {
      const payload = {
        label,
        type: 'universe',
        callback: created => {
          if (created?._id) {
            dispatch(saveAll({ item: created }));
          }
        },
      };
      if (!isAuthenticated) {
        payload.password = password;
      }
      const res = await createNewItem(payload);
      const created = res?.data?.item;
      if (created?._id) {
        setUniverses(prev => [
          created,
          ...prev.filter(u => u._id !== created._id),
        ]);
        setNewUniverse({ label: '', password: '' });
        dispatch(setOpenModales({ id: 'homeAddUniverse', open: false }));
        navigate(`/item/${created._id}`);
      }
    } catch (e) {
      setActionErrors({
        universe: "Impossible de créer l'univers.",
        favorite: '',
      });
    }
  };

  const handleAddFavorite = () => {
    const itemId = extractItemId(favoriteUrl);
    if (!itemId) {
      setActionErrors({
        universe: '',
        favorite: 'URL invalide. Exemple: /item/abc123',
      });
      return;
    }
    setActionErrors({ universe: '', favorite: '' });
    setFavorites(prev => (prev.includes(itemId) ? prev : [...prev, itemId]));
    setFavoriteUrl('');
  };

  const handleAddColumn = () => {
    setHomeColumns(prev => [
      ...prev,
      { id: uuid().replaceAll('-', ''), label: 'nouveau groupe', itemIds: [] },
    ]);
  };

  const handleDeleteColumn = columnToDelete => {
    if (!columnToDelete || columnToDelete.id === DEFAULT_COLUMN_ID) return;
    setHomeColumns(prev => {
      const next = cloneColumns(prev);
      const targetIndex = next.findIndex(
        column => column.id === DEFAULT_COLUMN_ID,
      );
      const deleteIndex = next.findIndex(
        column => column.id === columnToDelete.id,
      );
      if (deleteIndex === -1) return prev;
      const targetIdx = targetIndex >= 0 ? targetIndex : 0;
      if (next[deleteIndex]?.itemIds?.length > 0) {
        next[targetIdx].itemIds = [
          ...next[targetIdx].itemIds,
          ...next[deleteIndex].itemIds,
        ];
      }
      next.splice(deleteIndex, 1);
      return next;
    });
  };

  const handleStartEditColumn = column => {
    setEditingColumnId(column.id);
    setEditingColumnLabel(column.label ?? '');
  };

  const handleCancelEditColumn = () => {
    setEditingColumnId(null);
    setEditingColumnLabel('');
  };

  const handleCommitEditColumn = column => {
    const nextLabel = editingColumnLabel.trim();
    if (!column || !nextLabel) {
      handleCancelEditColumn();
      return;
    }
    setHomeColumns(prev =>
      prev.map(col =>
        col.id === column.id ? { ...col, label: nextLabel } : col,
      ),
    );
    handleCancelEditColumn();
  };

  const toggleColumnCollapse = columnId => {
    setCollapsedColumns(prev => ({
      ...prev,
      [columnId]: !prev[columnId],
    }));
  };

  const universeMap = useMemo(
    () => new Map(universes.map(universe => [universe._id, universe])),
    [universes],
  );

  const homeItemIds = useMemo(() => {
    const universeIds = universes.map(universe => universe._id);
    const adminCampaignIds = authUserId
      ? Object.values(items)
          .filter(item => item?.type === 'campaign')
          .filter(item => isUserInList(item?.access?.admins, authUserId))
          .filter(item => {
            const universe = items[item?.universe];
            return !isUniverseAdmin(universe, authUserId);
          })
          .map(item => item._id)
      : [];
    const favoriteIds = favorites.filter(favoriteId => {
      if (isGlobalAdmin()) {
        return true;
      }
      const item = items[favoriteId];
      if (!item) {
        return true;
      }
      const campaign = items[item?.campaign];
      const world = items[item?.world];
      const universe = items[item?.universe];
      const accessBlocksPublic =
        (item?.type === 'campaign' && item?.access?.visibility === 'private') ||
        (item?.type === 'universe' && item?.access?.visibility === 'private') ||
        campaign?.access?.visibility === 'private' ||
        universe?.access?.visibility === 'private';
      const legacyPublic =
        !accessBlocksPublic &&
        !item?.password &&
        (!item?.campaign || (campaign && !campaign?.password)) &&
        (!item?.world || (world && !world?.password)) &&
        (!item?.universe || (universe && !universe?.password));
      return canAccessItem(item, campaign, world, universe) || legacyPublic;
    });
    return Array.from(
      new Set([...universeIds, ...favoriteIds, ...adminCampaignIds]),
    );
  }, [favorites, universes, items]);

  const homeItemIdSet = useMemo(() => new Set(homeItemIds), [homeItemIds]);
  const homeItemsById = useMemo(() => {
    const map = new Map();
    homeItemIds.forEach(id => {
      const item = items[id] || universeMap.get(id);
      if (item?._id) {
        map.set(item._id, item);
      }
    });
    return map;
  }, [homeItemIds, items, universeMap]);

  const favoriteItems = useMemo(() => {
    return favorites.map(id => homeItemsById.get(id)).filter(Boolean);
  }, [favorites, homeItemsById]);

  const universeItems = useMemo(() => {
    return universes.filter(universe => homeItemIdSet.has(universe._id));
  }, [universes, homeItemIdSet]);

  const adminCampaignItems = useMemo(() => {
    if (!authUserId) return [];
    return Object.values(items)
      .filter(item => item?.type === 'campaign')
      .filter(item => isUserInList(item?.access?.admins, authUserId))
      .filter(item => {
        const universe = items[item?.universe];
        return !isUniverseAdmin(universe, authUserId);
      })
      .filter(item => homeItemIdSet.has(item._id));
  }, [items, authUserId, homeItemIdSet]);

  const autoSectionItems = useMemo(() => {
    const used = new Set([
      ...favoriteItems.map(item => item._id),
      ...universeItems.map(item => item._id),
      ...adminCampaignItems.map(item => item._id),
    ]);
    return Array.from(homeItemsById.values()).filter(
      item => !used.has(item._id),
    );
  }, [favoriteItems, universeItems, adminCampaignItems, homeItemsById]);
  const recentItems = useMemo(() => {
    return recentItemIds
      .map(id => homeItemsById.get(id) || items[id] || universeMap.get(id))
      .filter(Boolean);
  }, [recentItemIds, homeItemsById, items, universeMap]);
  const hiddenSet = useMemo(() => new Set(hiddenItemIds), [hiddenItemIds]);
  const visibleHomeItemsById = useMemo(() => {
    const map = new Map();
    Array.from(homeItemsById.entries()).forEach(([id, item]) => {
      if (!hiddenSet.has(id)) {
        map.set(id, item);
      }
    });
    return map;
  }, [homeItemsById, hiddenSet]);
  const pinnedItems = useMemo(() => {
    return pinnedItemIds
      .map(id => visibleHomeItemsById.get(id))
      .filter(Boolean);
  }, [pinnedItemIds, visibleHomeItemsById]);

  const normalizeStr = value =>
    String(value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');

  const filteredSections = useMemo(() => {
    const query = normalizeStr(homeSearch).trim();
    const hiddenItems = hiddenItemIds
      .map(id => homeItemsById.get(id) || items[id] || universeMap.get(id))
      .filter(Boolean);
    const sections = [
      { id: 'pinned', label: 'Épinglés', items: pinnedItems },
      {
        id: 'recent',
        label: 'Récents',
        items: recentItems.filter(item => !hiddenSet.has(item?._id)),
      },
      {
        id: 'favorites',
        label: 'Favoris',
        items: favoriteItems.filter(item => !hiddenSet.has(item?._id)),
      },
      {
        id: 'universes',
        label: 'Univers',
        items: universeItems.filter(item => !hiddenSet.has(item?._id)),
      },
      {
        id: 'admin-campaigns',
        label: 'Dossiers admin',
        items: adminCampaignItems.filter(item => !hiddenSet.has(item?._id)),
      },
      {
        id: 'other',
        label: 'Autres accès',
        items: autoSectionItems.filter(item => !hiddenSet.has(item?._id)),
      },
      {
        id: 'hidden',
        label: 'Masqués',
        items: hiddenItems,
      },
    ];

    const matchType = item => {
      if (homeTypeFilter === 'all') return true;
      if (homeTypeFilter === 'universe') return item?.type === 'universe';
      if (homeTypeFilter === 'campaign') return item?.type === 'campaign';
      if (homeTypeFilter === 'item') {
        return item?.type !== 'universe' && item?.type !== 'campaign';
      }
      return true;
    };

    const favoriteSet = new Set(favorites);
    const matchFavorite = (item, sectionId) =>
      sectionId === 'hidden' || !homeOnlyFavorites
        ? true
        : favoriteSet.has(item?._id);

    const matchSearch = item => {
      if (!query) return true;
      const parts = [item?.label, item?.alias, item?.type];
      const haystack = normalizeStr(parts.filter(Boolean).join(' '));
      return haystack.includes(query);
    };

    return sections
      .map(section => ({
        ...section,
        items: section.items.filter(
          item =>
            matchType(item) &&
            matchFavorite(item, section.id) &&
            matchSearch(item),
        ),
      }))
      .filter(
        section =>
          section.items.length > 0 ||
          section.id === 'favorites' ||
          section.id === 'universes',
      );
  }, [
    homeSearch,
    homeTypeFilter,
    homeOnlyFavorites,
    favoriteItems,
    pinnedItems,
    hiddenSet,
    universeItems,
    adminCampaignItems,
    autoSectionItems,
    favorites,
    hiddenItemIds,
    recentItems,
    homeItemsById,
    items,
    universeMap,
  ]);

  useEffect(() => {
    if (!isMobile || !Array.isArray(filteredSections)) {
      return;
    }
    setMobileSectionsCollapsed(prev => {
      const next = { ...prev };
      filteredSections.forEach(section => {
        if (next[section.id] === undefined) {
          next[section.id] = section.id !== 'pinned';
        }
      });
      return next;
    });
  }, [isMobile, filteredSections]);

  useEffect(() => {
    setStorageJSON('pinnedItemIds', pinnedItemIds);
  }, [pinnedItemIds]);

  useEffect(() => {
    if (!Array.isArray(history) || history.length === 0) return;
    setRecentItemIds(prev => {
      const next = Array.from(new Set([...history, ...prev])).slice(0, 100);
      return next;
    });
  }, [history]);

  useEffect(() => {
    setStorageJSON('recentItemIds', recentItemIds);
  }, [recentItemIds]);

  useEffect(() => {
    setStorageJSON('hiddenItemIds', hiddenItemIds);
  }, [hiddenItemIds]);

  useEffect(() => {
    if (!hasLoadedFavorites || !hasLoadedUniverses) return;
    setHomeColumns(prev => {
      let next = cloneColumns(prev);
      if (next.length === 0) {
        next = [
          {
            id: DEFAULT_COLUMN_ID,
            label: 'Sans groupe',
            itemIds: [],
          },
        ];
      }
      if (!next.find(column => column.id === DEFAULT_COLUMN_ID)) {
        next = [
          { id: DEFAULT_COLUMN_ID, label: 'Sans groupe', itemIds: [] },
          ...next,
        ];
      }
      const favoriteSet = new Set(favorites);
      const removedFavorites = Array.from(prevFavoritesRef.current).filter(
        id => !favoriteSet.has(id),
      );
      const removedFavoritesSet = new Set(removedFavorites);
      next = next.map(column => ({
        ...column,
        itemIds: Array.isArray(column.itemIds)
          ? column.itemIds.filter(id => !removedFavoritesSet.has(id))
          : [],
      }));
      const present = new Set(next.flatMap(column => column.itemIds));
      const newIds = homeItemIds.filter(
        id => !knownHomeItemIdsRef.current.has(id),
      );
      const missing = newIds.filter(id => !present.has(id));
      if (missing.length > 0) {
        const index = next.findIndex(column => column.id === DEFAULT_COLUMN_ID);
        const targetIndex = index >= 0 ? index : 0;
        const target = next[targetIndex];
        next[targetIndex] = {
          ...target,
          itemIds: [...target.itemIds, ...missing],
        };
      }
      prevFavoritesRef.current = favoriteSet;
      knownHomeItemIdsRef.current = new Set([
        ...knownHomeItemIdsRef.current,
        ...homeItemIds,
      ]);
      return next;
    });
  }, [hasLoadedFavorites, hasLoadedUniverses, homeItemIds, favorites]);

  const handleDragStart = event => {
    const { active } = event;
    if (active?.data?.current?.type === 'item') {
      document.body.style.cursor = 'grabbing';
      setActiveDragItemId(active.id);
    }
  };

  const handleDragCancel = () => {
    document.body.style.cursor = '';
    setActiveDragItemId(null);
  };

  const handleDragMove = () => {
    const container = columnsScrollRef.current;
    if (!container) return;
    const maxScrollLeft = container.scrollWidth - container.clientWidth;
    if (container.scrollLeft > maxScrollLeft) {
      container.scrollLeft = maxScrollLeft;
    } else if (container.scrollLeft < 0) {
      container.scrollLeft = 0;
    }
  };

  const handleScrollbarPointerDown = event => {
    const container = columnsScrollRef.current;
    if (!container) return;
    event.preventDefault();
    setIsDraggingScrollbar(true);
    scrollDragDataRef.current = {
      startX: event.clientX,
      startLeft: container.scrollLeft,
    };
  };

  useEffect(() => {
    if (!isDraggingScrollbar) return;

    const handlePointerMove = event => {
      const container = columnsScrollRef.current;
      if (!container) return;
      const { startX, startLeft } = scrollDragDataRef.current;
      const maxScrollLeft = container.scrollWidth - container.clientWidth;
      const trackWidth = container.clientWidth;
      if (trackWidth <= 0 || maxScrollLeft <= 0) return;
      const thumbWidth =
        (container.clientWidth / container.scrollWidth) * trackWidth;
      const maxThumbTravel = Math.max(trackWidth - thumbWidth, 1);
      const deltaX = event.clientX - startX;
      const nextLeft = startLeft + (deltaX / maxThumbTravel) * maxScrollLeft;
      container.scrollLeft = Math.max(0, Math.min(maxScrollLeft, nextLeft));
    };

    const handlePointerUp = () => {
      setIsDraggingScrollbar(false);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerUp);
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerUp);
    };
  }, [isDraggingScrollbar]);

  const handleDragOver = event => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    if (active?.data?.current?.type !== 'item') return;

    const activeColumnId = active.data.current?.columnId;
    const overType = over.data.current?.type;
    const overColumnId =
      overType === 'column' ? over.id : over.data.current?.columnId;

    if (!activeColumnId || !overColumnId || activeColumnId === overColumnId) {
      return;
    }

    setHomeColumns(columns => {
      const next = cloneColumns(columns);
      const fromColumn = next.find(column => column.id === activeColumnId);
      const toColumn = next.find(column => column.id === overColumnId);
      if (!fromColumn || !toColumn) return columns;

      const activeIndex = fromColumn.itemIds.indexOf(active.id);
      if (activeIndex === -1) return columns;

      fromColumn.itemIds.splice(activeIndex, 1);
      if (overType === 'column') {
        toColumn.itemIds.push(active.id);
      } else {
        const overIndex = toColumn.itemIds.indexOf(over.id);
        const insertIndex =
          overIndex >= 0 ? overIndex : toColumn.itemIds.length;
        toColumn.itemIds.splice(insertIndex, 0, active.id);
      }

      return next;
    });
  };

  const handleDragEnd = event => {
    const { active, over } = event;
    document.body.style.cursor = '';
    setActiveDragItemId(null);
    if (!over) return;

    const activeType = active?.data?.current?.type;
    const overType = over?.data?.current?.type;

    if (activeType === 'column') {
      const overColumnId =
        overType === 'column' ? over.id : over.data.current?.columnId;
      if (!overColumnId || active.id === overColumnId) return;
      setHomeColumns(columns => {
        const next = cloneColumns(columns);
        const oldIndex = next.findIndex(column => column.id === active.id);
        const newIndex = next.findIndex(column => column.id === overColumnId);
        if (oldIndex === -1 || newIndex === -1) return columns;
        return arrayMove(next, oldIndex, newIndex);
      });
      return;
    }

    if (activeType !== 'item') return;
    const activeColumnId = active.data.current?.columnId;
    const overColumnId =
      overType === 'column' ? over.id : over.data.current?.columnId;
    if (!activeColumnId || !overColumnId) return;

    if (activeColumnId === overColumnId && active.id !== over.id) {
      setHomeColumns(columns => {
        const next = cloneColumns(columns);
        const column = next.find(col => col.id === activeColumnId);
        if (!column) return columns;
        const oldIndex = column.itemIds.indexOf(active.id);
        const newIndex = column.itemIds.indexOf(over.id);
        if (oldIndex === -1 || newIndex === -1) return columns;
        column.itemIds = arrayMove(column.itemIds, oldIndex, newIndex);
        return next;
      });
    }
  };

  const getDraggedItemIdFromEvent = event => {
    try {
      const raw = event?.dataTransfer?.getData('application/x-estynoss-item');
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed?.type === 'item' ? parsed?.itemId : null;
    } catch (_e) {
      return null;
    }
  };

  const hasDraggedItemPayload = event => {
    const types = Array.from(event?.dataTransfer?.types || []);
    return types.includes('application/x-estynoss-item');
  };

  const applyDropToHomeSection = (sectionId, itemId) => {
    if (!itemId) return;
    if (sectionId === 'pinned') {
      setPinnedItemIds(prev =>
        prev.includes(itemId) ? prev : [...prev, itemId],
      );
      return;
    }
    if (sectionId === 'favorites') {
      setFavorites(prev => (prev.includes(itemId) ? prev : [...prev, itemId]));
      return;
    }
    if (sectionId === 'hidden') {
      setHiddenItemIds(prev =>
        prev.includes(itemId) ? prev : [...prev, itemId],
      );
    }
  };

  const removeFromHomeSection = (sectionId, itemId) => {
    if (!itemId) return;
    if (sectionId === 'pinned') {
      setPinnedItemIds(prev => prev.filter(id => id !== itemId));
      return;
    }
    if (sectionId === 'favorites') {
      setFavorites(prev => prev.filter(id => id !== itemId));
      return;
    }
    if (sectionId === 'hidden') {
      setHiddenItemIds(prev => prev.filter(id => id !== itemId));
    }
  };

  const renderHomeSection = section => (
    <div
      key={section.id}
      className={`w-full rounded-lg bg-main bg-opacity-40 p-1 mb-2 transition ${
        activeHomeDropSection === section.id ? 'ring-1 ring-secondary' : ''
      }`}
      onDragOver={event => {
        if (!HOME_DROP_SECTION_IDS.has(section.id)) return;
        if (!hasDraggedItemPayload(event)) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
        if (activeHomeDropSection !== section.id) {
          setActiveHomeDropSection(section.id);
        }
      }}
      onDragEnter={event => {
        if (!HOME_DROP_SECTION_IDS.has(section.id)) return;
        if (!hasDraggedItemPayload(event)) return;
        event.preventDefault();
        setActiveHomeDropSection(section.id);
      }}
      onDragLeave={() => {
        if (activeHomeDropSection === section.id) {
          setActiveHomeDropSection(null);
        }
      }}
      onDrop={event => {
        if (!HOME_DROP_SECTION_IDS.has(section.id)) return;
        event.preventDefault();
        const itemId = getDraggedItemIdFromEvent(event);
        applyDropToHomeSection(section.id, itemId);
        window.__homeDropSectionId = section.id;
        setActiveHomeDropSection(null);
      }}
    >
      <div
        className={`flex items-center gap-05 font-condensed font-bold text-sm uppercase text-contrast text-opacity-80 mb-05 ${
          isMobile || section.id === 'hidden' ? 'cursor-pointer select-none' : ''
        }`}
        onClick={() => {
          if (isMobile) {
            setMobileSectionsCollapsed(prev => ({
              ...prev,
              [section.id]: !prev[section.id],
            }));
            return;
          }
          if (section.id === 'hidden') {
            setHiddenSectionCollapsed(prev => !prev);
          }
        }}
      >
        {(isMobile || section.id === 'hidden') &&
          ((isMobile ? mobileSectionsCollapsed[section.id] : hiddenSectionCollapsed) ? (
            <FaChevronDown />
          ) : (
            <FaChevronUp />
          ))}
        {section.id === 'pinned' && <FaThumbtack />}
        {section.id === 'favorites' && <FaStar />}
        {section.id === 'hidden' && <FaEyeSlash />}
        <span className='flex-1'>{section.label}</span>
        {section.id === 'universes' && (
          <BtnRound
            className='w-3 h-3 bg-primary bg-opacity-80 hover:opacity-80 transition'
            onClick={() =>
              dispatch(setOpenModales({ id: 'homeAddUniverse', open: true }))
            }
          >
            <FaPlus />
          </BtnRound>
        )}
        {section.id === 'favorites' && (
          <BtnRound
            className='w-3 h-3 bg-primary bg-opacity-80 hover:opacity-80 transition'
            onClick={() =>
              dispatch(setOpenModales({ id: 'homeAddFavorite', open: true }))
            }
          >
            <FaPlus />
          </BtnRound>
        )}
        <span className='text-xs text-contrast text-opacity-50'>
          {section.items.length}
        </span>
      </div>
      {!((isMobile && mobileSectionsCollapsed[section.id]) || (!isMobile && section.id === 'hidden' && hiddenSectionCollapsed)) && (
        <div
          className={
            isMobile
              ? 'flex flex-col gap-1'
              : 'grid grid-cols-cards gap-1'
          }
        >
          {section.items.map(item => {
            const isPinned = pinnedItemIds.includes(item._id);
            const isHiddenSection = section.id === 'hidden';
            return (
              <div
                key={item._id}
                className='relative'
                draggable={HOME_DROP_SECTION_IDS.has(section.id)}
                onDragStart={event => {
                  if (!HOME_DROP_SECTION_IDS.has(section.id)) return;
                  const payload = {
                    type: 'item',
                    itemId: item?._id,
                    sourceFilterId: null,
                    sourceHomeSectionId: section.id,
                  };
                  event.dataTransfer.setData(
                    'application/x-estynoss-item',
                    JSON.stringify(payload),
                  );
                  event.dataTransfer.effectAllowed = 'move';
                  window.__homeDropSectionId = null;
                  setActiveHomeDropSection(null);
                }}
                onDragEnd={() => {
                  if (!HOME_DROP_SECTION_IDS.has(section.id)) return;
                  const dropSectionId = window.__homeDropSectionId || null;
                  // Dropped outside home drop zones => remove from source section.
                  if (!dropSectionId) {
                    removeFromHomeSection(section.id, item?._id);
                  }
                  window.__homeDropSectionId = null;
                  setActiveHomeDropSection(null);
                }}
              >
                <div className='absolute top-1 right-1 z-20 flex gap-05'>
                  {!isHiddenSection ? (
                    <>
                      <button
                        type='button'
                        className={`p-0 m-0 bg-transparent border-0 shadow-none transition hover:opacity-70 ${
                          isPinned
                            ? 'text-secondary'
                            : 'text-contrast text-opacity-70'
                        }`}
                        onClick={e => {
                          e.stopPropagation();
                          setPinnedItemIds(prev =>
                            prev.includes(item._id)
                              ? prev.filter(id => id !== item._id)
                              : [...prev, item._id],
                          );
                        }}
                        title={isPinned ? 'Retirer épinglé' : 'Épingler'}
                      >
                        <FaThumbtack
                          style={{
                            filter:
                              'drop-shadow(0 0 5px rgb(var(--main) / 0.85))',
                          }}
                        />
                      </button>
                      <button
                        type='button'
                        className='p-0 m-0 bg-transparent border-0 shadow-none transition hover:opacity-70 text-contrast text-opacity-70'
                        onClick={e => {
                          e.stopPropagation();
                          setHiddenItemIds(prev =>
                            prev.includes(item._id)
                              ? prev
                              : [...prev, item._id],
                          );
                        }}
                        title='Masquer cet item'
                      >
                        <FaEyeSlash
                          style={{
                            filter:
                              'drop-shadow(0 0 5px rgb(var(--main) / 0.85))',
                          }}
                        />
                      </button>
                    </>
                  ) : (
                    <button
                      type='button'
                      className='p-0 m-0 bg-transparent border-0 shadow-none transition hover:opacity-70 text-secondary'
                      onClick={e => {
                        e.stopPropagation();
                        setHiddenItemIds(prev =>
                          prev.filter(id => id !== item._id),
                        );
                      }}
                      title='Démasquer cet item'
                    >
                      <FaEye
                        style={{
                          filter:
                            'drop-shadow(0 0 3px rgb(var(--main) / 0.85))',
                        }}
                      />
                    </button>
                  )}
                </div>
                <CardItemHome
                  item={item}
                  isUniverse={item?.type === 'universe'}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  const favoritesSection = filteredSections.find(s => s.id === 'favorites');
  const universesSection = filteredSections.find(s => s.id === 'universes');
  const hiddenSection = filteredSections.find(s => s.id === 'hidden');
  const recentSection = filteredSections.find(s => s.id === 'recent');
  const hasAnySectionItem = filteredSections.some(
    section => (section?.items?.length || 0) > 0,
  );
  const mainSections = filteredSections.filter(s => {
    if (s.id === 'favorites' || s.id === 'universes') return false;
    if (s.id === 'hidden') return false;
    if (s.id === 'recent') return false;
    return true;
  });

  return (
    <div
      className={`bg-primary-d h-screen w-screen flex flex-col text-contrast ${
        isMobile ? 'gap-2' : 'gap-0'
      } relative ${isMobile ? 'overflow-auto' : 'overflow-hidden'}`}
    >
      <div className='w-screen h-screen flex items-center justify-center fixed z-0 opacity-10'>
        {logoSVG(theme, { maxHeight: '100%', padding: 100 })}
      </div>
      {isMobile && (
        <div className='w-full bg-main border-b border-main-d bg-opacity-60 flex justify-end px-1 items-center py-1 relative z-10 gap-2'>
          <ThemeChange />
          <NavigationManagePassword />
          <div
            className='transition hover:opacity-50 cursor-pointer'
            onClick={() =>
              dispatch(setOpenModales({ id: 'managePassword', open: true }))
            }
          >
            <FaKey />
          </div>
        </div>
      )}
      <ModalGlobal
        idToOpen={'homeAddUniverse'}
        title={'Créer un univers'}
      >
        <div className='flex flex-col gap-1'>
          <div className='text-sm font-bold uppercase text-contrast text-opacity-70'>
            Créer un univers
          </div>
          <div className='flex flex-wrap items-center gap-05'>
            <InputSimple
              initValue={newUniverse.label}
              placeholder={"Nom de l'univers"}
              onChange={e =>
                setNewUniverse(prev => ({ ...prev, label: e.target.value }))
              }
              onBlur={() => null}
            />
            {!authUserId && (
              <InputSimple
                initValue={newUniverse.password}
                placeholder={'Mot de passe'}
                type={'text'}
                onChange={e =>
                  setNewUniverse(prev => ({
                    ...prev,
                    password: e.target.value,
                  }))
                }
                onBlur={() => null}
              />
            )}
            <BtnSimple
              sm
              onClick={handleCreateUniverse}
            >
              Créer
            </BtnSimple>
          </div>
          {actionErrors.universe && (
            <div className='text-xs text-red-400'>{actionErrors.universe}</div>
          )}
          {!authUserId && !isGlobalAdmin() && (
            <div className='text-xs text-contrast text-opacity-60'>
              8 caractères minimum dont 1 caractère spécial.
            </div>
          )}
          {authUserId && (
            <div className='text-xs text-contrast text-opacity-60'>
              Univers privé lié à votre compte (owner).
            </div>
          )}
        </div>
      </ModalGlobal>
      <ModalGlobal
        idToOpen={'homeAddFavorite'}
        title={'Ajouter un favori'}
      >
        <div className='flex flex-col gap-1'>
          <div className='text-sm font-bold uppercase text-contrast text-opacity-70'>
            Ajouter un favori par URL
          </div>
          <div className='flex flex-wrap items-center gap-05'>
            <InputSimple
              initValue={favoriteUrl}
              placeholder={'https://.../item/xxxx'}
              onChange={e => setFavoriteUrl(e.target.value)}
              onBlur={() => null}
            />
            <BtnSimple
              sm
              onClick={handleAddFavorite}
            >
              Ajouter
            </BtnSimple>
          </div>
          {actionErrors.favorite && (
            <div className='text-xs text-red-400'>{actionErrors.favorite}</div>
          )}
        </div>
      </ModalGlobal>
      <div
        className={`w-full relative z-10 ${
          isMobile
            ? 'px-2 overflow-auto'
            : 'px-0 flex flex-1 min-h-0 overflow-hidden'
        }`}
      >
        {HOME_V2_ENABLED ? (
          <div
            className={`w-full ${isMobile ? 'pb-3' : 'h-full min-h-0 flex'}`}
          >
            <div
              className={`${
                isMobile
                  ? 'flex flex-col gap-2'
                  : 'flex-1 min-h-0 overflow-y-auto pb-3 px-2 pt-2'
              }`}
            >
              <div className='flex flex-wrap items-center gap-05 mb-2'>
                <FaSearch />
                <InputSimple
                  className='flex-1 min-w-180'
                  initValue={homeSearch}
                  placeholder='Rechercher un item...'
                  onChange={e => setHomeSearch(e.target.value)}
                  onBlur={() => null}
                />
                <div className='flex flex-wrap justify-center items-center items-center gap-05'>
                  {[
                    { id: 'all', label: 'Tout' },
                    { id: 'universe', label: 'Univers' },
                    { id: 'campaign', label: 'Dossiers' },
                    { id: 'item', label: 'Items' },
                  ].map(option => (
                    <BtnSimple
                      key={option.id}
                      sm
                      color={homeTypeFilter === option.id ? 'secondary' : null}
                      border={homeTypeFilter === option.id ? 'secondary' : null}
                      onClick={() => setHomeTypeFilter(option.id)}
                    >
                      {option.label}
                    </BtnSimple>
                  ))}
                  <BtnSimple
                    sm
                    color={homeOnlyFavorites ? 'secondary' : null}
                    border={homeOnlyFavorites ? 'secondary' : null}
                    onClick={() => setHomeOnlyFavorites(prev => !prev)}
                  >
                    Favoris uniquement
                  </BtnSimple>
                </div>
              </div>
              {mainSections.map(section => renderHomeSection(section))}
              {(universesSection || favoritesSection) && (
                <div className='flex flex-col md:flex-row gap-1'>
                  {universesSection && (
                    <div className='flex-1 min-w-0'>
                      {renderHomeSection(universesSection)}
                    </div>
                  )}
                  {favoritesSection && (
                    <div className='flex-1 min-w-0'>
                      {renderHomeSection(favoritesSection)}
                    </div>
                  )}
                </div>
              )}
              {hiddenSection && renderHomeSection(hiddenSection)}
              {isMobile && recentSection && renderHomeSection(recentSection)}
              {!hasAnySectionItem && (
                <div className='w-full rounded-lg bg-main bg-opacity-30 p-2 text-sm text-contrast text-opacity-60'>
                  Aucun résultat.
                </div>
              )}
            </div>
            {!isMobile && (
              <div className='w-200 min-w-200 h-full min-h-0 bg-main border-l border-main-d border-opacity-20 flex flex-col overflow-hidden'>
                <div className='sticky top-0 backdrop-blur-md bg-main bg-opacity-50 flex items-center gap-1 font-condensed font-bold text-lg px-05'>
                  <FaHistory />
                  Historique
                </div>
                <div className='flex-1 overflow-auto'>
                  {(recentSection?.items || []).map(item => (
                    <NavigationDisplayItems
                      key={item?._id}
                      displayItem={item}
                      big={true}
                      navigateOnClick={true}
                      hideChevron={true}
                    />
                  ))}
                </div>
                <div
                  className='w-full flex justify-evenly border-t border-main-d py-05 bg-main gap-1 sticky bottom-0 z-10'
                  style={{
                    paddingBottom: shouldOffsetHistoryFooter
                      ? 'calc(env(safe-area-inset-bottom, 0px) + 36px)'
                      : 'env(safe-area-inset-bottom, 0px)',
                  }}
                >
                  <ThemeChange />
                  <NavigationManagePassword />
                  <div
                    className='transition hover:opacity-50 cursor-pointer'
                    onClick={() =>
                      dispatch(
                        setOpenModales({ id: 'managePassword', open: true }),
                      )
                    }
                  >
                    <FaKey />
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : isMobile ? (
          <div className='flex flex-col gap-2 pb-4'>
            {homeColumns.map(column => (
              <div
                key={column.id}
                className='w-full rounded-lg bg-main bg-opacity-40 p-1'
              >
                <div
                  className='flex items-center gap-05 font-condensed font-bold text-sm uppercase text-contrast text-opacity-80 mb-05 cursor-pointer select-none'
                  onClick={() => toggleColumnCollapse(column.id)}
                >
                  {collapsedColumns[column.id] ? (
                    <FaChevronDown />
                  ) : (
                    <FaChevronUp />
                  )}
                  <span className='flex-1'>{column.label}</span>
                </div>
                {!collapsedColumns[column.id] && (
                  <div className='flex flex-col gap-1'>
                    {column.itemIds
                      .filter(itemId => homeItemIdSet.has(itemId))
                      .map(itemId => {
                        const item = items[itemId] || universeMap.get(itemId);
                        if (!item) return null;
                        return (
                          <CardItemHome
                            key={itemId}
                            item={item}
                            isUniverse={item?.type === 'universe'}
                          />
                        );
                      })}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
              modifiers={[restrictColumnDrag, restrictToScrollForColumns]}
              autoScroll={true}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDragEnd={handleDragEnd}
              onDragCancel={handleDragCancel}
              onDragMove={handleDragMove}
            >
              <SortableContext
                items={homeColumns.map(column => column.id)}
                strategy={horizontalListSortingStrategy}
              >
                <div
                  ref={columnsScrollRef}
                  className='w-full h-full overflow-x-auto overflow-y-hidden pb-3'
                >
                  <div className='flex flex-nowrap items-stretch gap-2 min-w-max h-full'>
                    {homeColumns.map(column => (
                      <HomeSortableColumn
                        key={column.id}
                        column={column}
                        onDelete={handleDeleteColumn}
                        isEditing={editingColumnId === column.id}
                        editingLabel={editingColumnLabel}
                        onStartEdit={handleStartEditColumn}
                        onChangeEdit={setEditingColumnLabel}
                        onCommitEdit={handleCommitEditColumn}
                        onCancelEdit={handleCancelEditColumn}
                      >
                        <SortableContext
                          items={column.itemIds}
                          strategy={rectSortingStrategy}
                        >
                          {column.itemIds.length === 0 && (
                            <div className='text-xs text-contrast text-opacity-50 px-05 py-1 rounded bg-main bg-opacity-30'>
                              Glissez un item ici
                            </div>
                          )}
                          {column.itemIds
                            .filter(itemId => homeItemIdSet.has(itemId))
                            .map(itemId => {
                              const item =
                                items[itemId] || universeMap.get(itemId);
                              if (!item) return null;
                              return (
                                <HomeSortableItem
                                  key={itemId}
                                  item={item}
                                  columnId={column.id}
                                />
                              );
                            })}
                        </SortableContext>
                      </HomeSortableColumn>
                    ))}
                  </div>
                </div>
              </SortableContext>
              <DragOverlay>
                {activeDragItemId ? (
                  <div className='w-300'>
                    <CardItemHome
                      item={
                        items[activeDragItemId] ||
                        universeMap.get(activeDragItemId)
                      }
                      isUniverse={
                        items[activeDragItemId]?.type === 'universe' ||
                        universeMap.get(activeDragItemId)?.type === 'universe'
                      }
                    />
                  </div>
                ) : null}
              </DragOverlay>
            </DndContext>
            {scrollMetrics.scrollWidth > scrollMetrics.clientWidth && (
              <div className='fixed left-2 right-2 bottom-2 h-1 flex items-center z-50 pointer-events-none'>
                <div
                  className='w-full h-1 bg-main bg-opacity-10 rounded relative cursor-pointer pointer-events-auto'
                  onPointerDown={e => {
                    const container = columnsScrollRef.current;
                    if (!container) return;
                    const rect = e.currentTarget.getBoundingClientRect();
                    const clickX = e.clientX - rect.left;
                    const maxScrollLeft =
                      container.scrollWidth - container.clientWidth;
                    const trackWidth = rect.width;
                    const thumbWidth = Math.max(
                      (container.clientWidth / container.scrollWidth) *
                        trackWidth,
                      24,
                    );
                    const maxThumbTravel = Math.max(trackWidth - thumbWidth, 1);
                    const thumbLeft = Math.min(
                      maxThumbTravel,
                      Math.max(0, clickX - thumbWidth / 2),
                    );
                    const nextLeft =
                      (thumbLeft / maxThumbTravel) * maxScrollLeft;
                    container.scrollLeft = nextLeft;
                  }}
                >
                  <div
                    className={`h-05 rounded bg-contrast bg-opacity-10 hover:bg-opacity-30 transition absolute top-0 ${
                      isDraggingScrollbar ? 'bg-opacity-50' : ''
                    }`}
                    style={{
                      width: `${Math.max(
                        (scrollMetrics.clientWidth /
                          scrollMetrics.scrollWidth) *
                          100,
                        10,
                      )}%`,
                      left: `${
                        (scrollMetrics.scrollLeft /
                          Math.max(
                            scrollMetrics.scrollWidth -
                              scrollMetrics.clientWidth,
                            1,
                          )) *
                        (100 -
                          Math.max(
                            (scrollMetrics.clientWidth /
                              scrollMetrics.scrollWidth) *
                              100,
                            10,
                          ))
                      }%`,
                    }}
                    onPointerDown={handleScrollbarPointerDown}
                  />
                </div>
              </div>
            )}
          </>
        )}
      </div>
      {!HOME_V2_ENABLED && (
        <div
          className={`fixed right-3 top-1/2 -translate-y-1/2 z-50 ${
            isMobile ? 'hidden' : ''
          }`}
        >
          <button
            className='rounded-full bg-primary bg-opacity-70 p-1 shadow text-contrast text-opacity-80 hover:text-opacity-100 transition'
            onClick={handleAddColumn}
            title='ajouter un groupe'
            aria-label='ajouter un groupe'
          >
            <FaPlus size={22} />
          </button>
        </div>
      )}
    </div>
  );
}

export default Home;
